import { useHudStore } from "../../store/hudStore";
import { MOODS } from "../../data/seed";
import { bloom } from "../ui";

// Health module: mood, body, and mind (app.html healthModule, lines 1049–1070).
export default function HealthModule() {
  const st = useHudStore();
  const meta = (k?: string) => MOODS.find((m) => m.key === k);
  const today = st.moodWeek[st.moodWeek.length - 1];
  const tMeta = today && meta(today.mood);
  const moodToday = tMeta ? tMeta.label : "Not logged yet";
  const moodTodayColor = tMeta ? tMeta.color : "var(--mut)";
  const rank: Record<string, number> = { rough: 1, low: 2, ok: 3, good: 4, great: 5 };
  const vals = st.moodWeek.filter((m) => m.mood).map((m) => rank[m.mood ?? ""] ?? 0);
  let trend = "Not enough logged to read a pattern yet.";
  if (vals.length >= 3) {
    const h = Math.ceil(vals.length / 2);
    const first = vals.slice(0, h).reduce((a, b) => a + b, 0) / h;
    const last = vals.slice(-h).reduce((a, b) => a + b, 0) / h;
    trend =
      last > first + 0.4
        ? "Lifting since the weekend."
        : last < first - 0.4
          ? "Two low days in a row: worth noticing, not fixing."
          : "Steady week, no big swings.";
  }
  const bodyCount = st.bodyWeek.filter((b) => b.done).length;

  return (
    <div style={{ background: "var(--card)", border: "1px solid var(--line)", borderRadius: 10, padding: "20px clamp(16px,2vw,22px) 22px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--mut)" }}>How you've been</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 11, marginTop: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: "clamp(24px,3.4vw,30px)", fontWeight: 700, fontFamily: "var(--display)", letterSpacing: "-0.01em", lineHeight: 1, color: moodTodayColor }}>{moodToday}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--mut)" }}>today</span>
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--mut)", marginTop: 8, textWrap: "pretty" }}>{trend}</div>
        </div>
        <button type="button" onClick={st.openMood} style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, color: "var(--accent-ink)", background: "var(--raise)", border: "1px solid var(--control-edge)", padding: "10px 15px", borderRadius: 999, cursor: "pointer" }}>
          <i className="fa-solid fa-pen" style={{ fontSize: 11, lineHeight: 1 }} />How are you today?
        </button>
      </div>

      {st.moodPick && (
        <div style={{ display: "flex", gap: 9, flexWrap: "wrap", marginTop: 16, padding: 14, borderRadius: 10, background: "var(--card2)", border: "1px solid var(--line2)" }}>
          {MOODS.map((m) => (
            <button key={m.key} type="button" onClick={() => st.setMood(m.key)} style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "13px 18px", borderRadius: 10, background: "var(--card)", border: "1px solid var(--control-edge)", cursor: "pointer" }}>
              <i className={m.fa} style={{ fontSize: 17, lineHeight: 1, color: m.color }} />
              <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--sub)" }}>{m.label}</span>
            </button>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 18 }}>
        {st.moodWeek.map((m) => {
          const mm = meta(m.mood);
          return (
            <div key={m.day} style={{ flex: "1 1 78px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "13px 8px", borderRadius: 10, background: mm ? "rgba(255,255,255,.04)" : "transparent", border: `1px solid ${mm ? mm.color : "var(--line)"}` }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--mut)" }}>{m.day}</span>
              <i className={mm ? mm.fa : "fa-regular fa-circle"} style={{ fontSize: 16, lineHeight: 1, color: mm ? mm.color : "var(--line2)" }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--mut)" }}>{mm ? mm.label : "not yet"}</span>
            </div>
          );
        })}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 18, marginTop: 24 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 12.5, fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: "var(--mut)" }}>
            <i className="fa-solid fa-heart-pulse" style={{ fontSize: 12, lineHeight: 1, color: "var(--pink)" }} />Body
          </div>
          <div style={{ display: "flex", gap: 7, marginTop: 14 }}>
            {st.bodyWeek.map((b) => (
              <div key={b.day} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 7 }}>
                <span style={{ width: "100%", height: 34, borderRadius: 8, background: b.done ? "var(--pink)" : "var(--raise)", border: b.done ? "none" : "1px solid var(--line)" }} />
                <span style={{ fontSize: 10, fontWeight: 700, color: b.done ? "var(--ink)" : "var(--mut)" }}>{b.day}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--mut)", marginTop: 12 }}>{bodyCount} of 7 days moved</div>
        </div>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 12.5, fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: "var(--mut)" }}>
            <i className="fa-solid fa-brain" style={{ fontSize: 12, lineHeight: 1, color: "var(--sub)" }} />Mind
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 14 }}>
            {st.mindCare.map((mc) => (
              <div key={mc.label}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <i className={mc.fa} style={{ fontSize: 12, lineHeight: 1, flex: "0 0 auto", color: mc.color }} />
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--sub)", minWidth: 0 }}>{mc.label}</span>
                  <span style={{ marginLeft: "auto", flex: "0 0 auto", fontSize: 11.5, fontWeight: 700, color: mc.color }}>{mc.days} / {mc.of}</span>
                </div>
                <div style={{ height: 6, borderRadius: 999, background: "var(--raise)", overflow: "hidden", marginTop: 7 }}>
                  <span style={{ display: "block", height: "100%", borderRadius: 999, background: mc.color, boxShadow: bloom(mc.color, (mc.days / mc.of) * 100, 10), width: Math.round((mc.days / mc.of) * 100) + "%" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--mut)", marginTop: 18, textWrap: "pretty" }}>A rough day logged is worth the same XP as a great one. The point is noticing, not performing. Nothing here is a diagnosis.</div>
    </div>
  );
}
